/**
 * Matching Set widget.
 *
 * Adds every ticked product — the current one included — to the cart in one
 * request, keeps a running total, and keeps sizes matched across the set.
 *
 * The figures shown here are display only. The real discount is a native Shopify
 * "Buy X Get Y" automatic discount on the matching-set collection: 10% off ONE
 * item, and Shopify applies it to the cheapest qualifying line. The total below
 * mirrors that, so the page never promises more than checkout gives.
 */
(function () {
  'use strict';

  var ROOT = '.hdt-matching-set';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function MatchingSet(el) {
    this.el = el;
    this.percent = parseFloat(el.dataset.percent) || 0;
    this.minItems = parseInt(el.dataset.minItems, 10) || 2;
    this.syncSizes = el.dataset.syncSizes === 'true';
    this.format = el.dataset.moneyFormat || '{{amount}}';

    this.items = Array.prototype.slice.call(el.querySelectorAll('[data-set-item]'));
    this.button = el.querySelector('[data-set-add]');
    this.note = el.querySelector('[data-set-note]');
    this.was = el.querySelector('[data-set-was]');
    this.now = el.querySelector('[data-set-now]');
    this.save = el.querySelector('[data-set-save]');

    this.bind();
    this.syncFromCurrent();
    this.render();

    // Fallback pages start hidden and only appear if suggestions are found.
    if (el.hasAttribute('data-fallback')) this.loadSuggestions();
  }

  /**
   * Suggestions for products that belong to no design set.
   *
   * These are NOT a design set and earn no discount, so the markup carries no
   * badge and no savings line — the block renders with percent 0.
   *
   * Same-size matches come first: a customer looking at a size 38 shoe is shown
   * other things available in 38. If nothing matches, any in-stock suggestion is
   * used rather than showing an empty box.
   */
  MatchingSet.prototype.loadSuggestions = function () {
    var self = this;
    var id = this.el.dataset.productId;
    if (!id) return;

    var url = window.Shopify.routes.root + 'recommendations/products.json?product_id=' +
      encodeURIComponent(id) + '&limit=10&intent=related';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.products) || [];
        var wanted = self.currentSize();

        var usable = products.filter(function (p) {
          return p.variants.some(function (v) { return v.available; });
        });
        if (!usable.length) return;

        var sameSize = usable.filter(function (p) {
          return wanted && p.variants.some(function (v) {
            return v.available && String(v.title).trim() === wanted;
          });
        });

        var picked = (sameSize.length ? sameSize : usable).slice(0, 2);
        if (!picked.length) return;

        picked.forEach(function (p) { self.appendSuggestion(p, wanted); });

        self.items = Array.prototype.slice.call(self.el.querySelectorAll('[data-set-item]'));
        self.el.removeAttribute('hidden');
        self.render();
      })
      .catch(function () {
        /* Leave the block hidden — better than an empty box. */
      });
  };

  /** The size the customer is on for the main product, or '' if none chosen. */
  MatchingSet.prototype.currentSize = function () {
    var input = this.el.querySelector('[data-current-size]');
    return input ? String(input.dataset.currentSize || '').trim() : '';
  };

  MatchingSet.prototype.appendSuggestion = function (p, wanted) {
    var list = this.el.querySelector('.hdt-matching-set__list');
    if (!list) return;

    var available = p.variants.filter(function (v) { return v.available; });
    if (!available.length) return;

    var li = document.createElement('li');
    li.className = 'hdt-matching-set__item';
    li.setAttribute('data-set-item', '');

    var img = p.featured_image ? '<img src="' + p.featured_image + '&width=200" alt="" loading="lazy">' : '';

    var control;
    if (available.length > 1) {
      var opts = available.map(function (v) {
        var sel = wanted && String(v.title).trim() === wanted ? ' selected' : '';
        return '<option value="' + v.id + '" data-price="' + v.price + '" data-size="' +
          escapeHtml(String(v.title)) + '"' + sel + '>' + escapeHtml(String(v.title)) + '</option>';
      }).join('');
      var placeholderSelected = wanted && available.some(function (v) {
        return String(v.title).trim() === wanted;
      }) ? '' : ' selected';
      control = '<select class="hdt-matching-set__select" data-set-variant data-syncable>' +
        '<option value="" data-price="0" data-size=""' + placeholderSelected + '>' +
        escapeHtml(this.chooseSizeLabel()) + '</option>' + opts + '</select>';
    } else {
      control = '<input type="hidden" data-set-variant value="' + available[0].id +
        '" data-price="' + available[0].price + '">';
    }

    li.innerHTML =
      '<label class="hdt-matching-set__check">' +
        '<input type="checkbox" data-set-toggle aria-label="' + escapeHtml(p.title) + '">' +
        '<span class="hdt-matching-set__box" aria-hidden="true"></span>' +
      '</label>' +
      '<a class="hdt-matching-set__media" href="' + p.url + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
      '<div class="hdt-matching-set__info">' +
        '<a class="hdt-matching-set__name" href="' + p.url + '">' + escapeHtml(p.title) + '</a>' +
        '<span class="hdt-matching-set__meta">' + control +
          '<span class="hdt-matching-set__price" data-set-price></span>' +
        '</span>' +
      '</div>';

    list.appendChild(li);
  };

  MatchingSet.prototype.chooseSizeLabel = function () {
    var existing = this.el.querySelector('select[data-syncable] option[value=""]');
    return existing ? existing.textContent.trim() : 'Choose size';
  };

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  MatchingSet.prototype.bind = function () {
    var self = this;

    // A size chosen on a row applies to that row only. It is deliberately not
    // pushed to the other rows or back to the main product — the customer may
    // genuinely want a different size for a different item.
    this.el.addEventListener('change', function (e) {
      if (e.target.matches('[data-set-toggle]') || e.target.matches('[data-set-variant]')) {
        self.render();
      }
    });

    this.button.addEventListener('click', function () {
      self.add();
    });

    // The theme republishes the main product's variant whenever the customer
    // changes size on the product form. Ecomus emits 'variant:change' from the
    // product form; the others are listed as a safety net across theme versions.
    ['variant:change', 'variant:updated', 'variant:update', 'variant:selected'].forEach(function (name) {
      document.addEventListener(name, function (e) {
        var variant = e.detail && e.detail.variant;
        if (!variant) return;
        self.setCurrentVariant(variant);
        if (self.syncSizes) self.applySize(String(variant.title || '').trim());
        self.render();
      }, true);
    });
  };

  /**
   * Record the variant the customer picked on the main product form, so the set
   * posts the size they actually chose rather than a Liquid-time default.
   */
  MatchingSet.prototype.setCurrentVariant = function (variant) {
    var input = this.el.querySelector('[data-current-size]');
    if (!input || !variant.id) return;
    input.value = String(variant.id);
    input.dataset.currentSize = String(variant.title || '').trim();
    if (variant.price != null) input.dataset.price = String(variant.price);
  };

  /** The chosen size label for a control, or '' when it has no options. */
  MatchingSet.prototype.sizeOf = function (control) {
    if (control.tagName !== 'SELECT') return '';
    var opt = control.options[control.selectedIndex];
    return opt ? String(opt.dataset.size || opt.textContent).trim() : '';
  };

  /**
   * On load the rows stay on the placeholder unless the customer arrived with a
   * variant already chosen (?variant=... in the URL). Liquid leaves
   * data-current-size empty otherwise, so nothing is guessed on their behalf.
   */
  MatchingSet.prototype.syncFromCurrent = function () {
    if (!this.syncSizes) return;
    var current = this.el.querySelector('[data-current-size]');
    if (!current) return;
    var size = String(current.dataset.currentSize || '').trim();
    if (size) this.applySize(size);
  };

  /**
   * Put every row on `size`, where that row actually offers it. Rows that do not
   * stock that size are left alone rather than forced onto something else.
   */
  MatchingSet.prototype.applySize = function (size) {
    if (!size) return;
    var selects = this.el.querySelectorAll('select[data-syncable]');

    Array.prototype.forEach.call(selects, function (select) {
      for (var i = 0; i < select.options.length; i++) {
        var candidate = String(select.options[i].dataset.size || '').trim();
        if (candidate && candidate === size) {
          select.selectedIndex = i;
          break;
        }
      }
    });
  };

  /** Variant id + price for a row, or null when the row is not usable. */
  MatchingSet.prototype.read = function (item) {
    var control = item.querySelector('[data-set-variant]');
    if (!control) return null;

    var id;
    var price;

    if (control.tagName === 'SELECT') {
      var opt = control.options[control.selectedIndex];
      if (!opt) return null;
      id = opt.value;
      price = parseInt(opt.dataset.price, 10);
    } else {
      id = control.value;
      price = parseInt(control.dataset.price, 10);
    }

    if (!id) return null;
    return { id: id, price: isNaN(price) ? 0 : price };
  };

  MatchingSet.prototype.selected = function () {
    var self = this;
    return this.items.filter(function (item) {
      var toggle = item.querySelector('[data-set-toggle]');
      return toggle && toggle.checked && self.read(item);
    });
  };

  MatchingSet.prototype.render = function () {
    var self = this;
    var chosen = this.selected();

    // Keep each row's price label in step with its chosen size.
    this.items.forEach(function (item) {
      var data = self.read(item);
      var label = item.querySelector('[data-set-price]');
      if (data && label) label.textContent = money(data.price, self.format);
    });

    var prices = chosen.map(function (item) { return self.read(item).price; });
    var gross = prices.reduce(function (sum, p) { return sum + p; }, 0);

    // The discount is Buy X Get Y: Shopify takes the percentage off ONE item,
    // and picks the cheapest qualifying line. Mirror that exactly - taking it
    // off the whole total would overstate the saving on the page.
    var qualifies = chosen.length >= this.minItems && this.percent > 0;
    var cheapest = prices.length ? Math.min.apply(null, prices) : 0;
    var discount = qualifies ? Math.round(cheapest * this.percent / 100) : 0;
    var net = gross - discount;

    this.now.textContent = money(net, this.format);

    if (qualifies) {
      this.was.textContent = money(gross, this.format);
      this.was.hidden = false;
      this.save.textContent = (this.save.dataset.template || '[amount]')
        .replace('[amount]', money(gross - net, this.format));
      this.save.hidden = false;
    } else {
      this.was.hidden = true;
      this.save.hidden = true;
    }

    // A ticked row with no size yet cannot be posted. Say so rather than
    // silently dropping it from the set the customer thinks they are buying.
    // This covers the main product too: its hidden input stays empty until the
    // customer picks a size on the product form.
    var awaitingSize = this.items.filter(function (item) {
      var toggle = item.querySelector('[data-set-toggle]');
      if (!toggle || !toggle.checked) return false;

      var select = item.querySelector('select[data-syncable]');
      if (select) return !select.value;

      var current = item.querySelector('[data-needs-size="true"]');
      return !!current && !current.value;
    });

    if (awaitingSize.length) {
      this.note.textContent = this.note.dataset.needsSize || '';
      this.note.hidden = false;
    } else if (!this.note.dataset.error) {
      this.note.hidden = true;
    }

    this.button.disabled = chosen.length === 0 || awaitingSize.length > 0;
  };

  /**
   * Open the theme's cart drawer the way the theme itself does — by triggering
   * its own opener — rather than reaching into the drawer's internals, which
   * differ between Ecomus versions. Falls back to the cart page if the drawer
   * is switched off in theme settings.
   */
  MatchingSet.prototype.openCartDrawer = function () {
    var opener = document.querySelector('[aria-controls="CartDrawer"]');
    if (opener) {
      // Let the drawer re-render from cart:reload before it slides open.
      setTimeout(function () { opener.click(); }, 120);
      return;
    }
    window.location.href = window.Shopify.routes.root + 'cart';
  };

  MatchingSet.prototype.add = function () {
    var self = this;

    // Every ticked row is posted, the current product included, so one click
    // really does add the whole set.
    var lines = this.selected().map(function (item) {
      return { id: self.read(item).id, quantity: 1 };
    });

    if (!lines.length) return;

    this.button.setAttribute('disabled', 'true');
    this.button.setAttribute('aria-busy', 'true');
    this.note.hidden = true;

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok) throw new Error(result.body.description || result.body.message || 'Could not add to cart');
        document.dispatchEvent(new CustomEvent('cart:reload'));
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        self.openCartDrawer();
      })
      .catch(function (err) {
        self.note.dataset.error = '1';
        self.note.textContent = err.message;
        self.note.hidden = false;
      })
      .finally(function () {
        self.button.removeAttribute('aria-busy');
        self.render();
      });
  };

  function init(scope) {
    var nodes = (scope || document).querySelectorAll(ROOT);
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.setReady) return;
      el.dataset.setReady = 'true';
      new MatchingSet(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init when the block is added or moved.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    init(e.target);
  });
})();
